import { rotationFromDirection } from "@novorender/api";
import { quat, ReadonlyQuat, ReadonlyVec3, vec3 } from "gl-matrix";

import { getCameraDir } from "features/engine2D/utils";
import { degToRad } from "utils/math";

export const CAMERA_FLY_TIME = 1000;

export interface AxisSphereDirection {
    rotation: ReadonlyQuat;
}

/** Build a camera rotation quat from a look direction, with optional Z-axis rotation. */
export function makeRotation(direction: ReadonlyVec3, zRotation = 0): quat {
    const rot = rotationFromDirection(direction);
    if (zRotation !== 0) {
        quat.rotateZ(rot, rot, zRotation);
    }
    return rot;
}

const SQRT3 = 1 / Math.sqrt(3);

export const AXIS_SPHERE_CAMERA_DIRECTIONS: Record<string, AxisSphereDirection> = {
    // Cardinal directions (faces)
    // Rotation quats represent camera orientation when looking from each direction
    // Coordinate system: X = right/left, Y = back/front, Z = top/bottom
    part_front: { rotation: makeRotation([0, -1, 0]) },
    part_back: { rotation: makeRotation([0, 1, 0]) },
    part_top: { rotation: makeRotation([0, 0, 1]) },
    part_bottom: { rotation: makeRotation([0, 0, -1], Math.PI) },
    part_right: { rotation: makeRotation([1, 0, 0]) },
    part_left: { rotation: makeRotation([-1, 0, 0]) },

    part_n: { rotation: makeRotation([0, 0, 1]) },
    part_n_bottom: { rotation: makeRotation([0, 0, -1]) },
    part_s: { rotation: makeRotation([0, 0, 1], Math.PI) },
    part_s_bottom: { rotation: makeRotation([0, 0, -1], Math.PI) },
    part_w: { rotation: makeRotation([0, 0, 1], Math.PI / 2) },
    part_w_bottom: { rotation: makeRotation([0, 0, -1], -Math.PI / 2) },
    part_e: { rotation: makeRotation([0, 0, 1], -Math.PI / 2) },
    part_e_bottom: { rotation: makeRotation([0, 0, -1], Math.PI / 2) },

    // Corner directions (vertices)
    part_top_front_left: { rotation: makeRotation([-SQRT3, -SQRT3, SQRT3]) },
    part_top_front_right: { rotation: makeRotation([SQRT3, -SQRT3, SQRT3]) },
    part_top_back_left: { rotation: makeRotation([-SQRT3, SQRT3, SQRT3]) },
    part_top_back_right: { rotation: makeRotation([SQRT3, SQRT3, SQRT3]) },
    part_bottom_front_left: { rotation: makeRotation([-SQRT3, -SQRT3, -SQRT3]) },
    part_bottom_front_right: { rotation: makeRotation([SQRT3, -SQRT3, -SQRT3]) },
    part_bottom_back_left: { rotation: makeRotation([-SQRT3, SQRT3, -SQRT3]) },
    part_bottom_back_right: { rotation: makeRotation([SQRT3, SQRT3, -SQRT3]) },
};

/**
 * Compute the target camera rotation for a given axis sphere part click.
 * Returns null if the click should be ignored (unknown part, already at rotation).
 */
export function computeTargetRotation(partName: string, currentCameraRotation: ReadonlyQuat): ReadonlyQuat | null {
    const dir = AXIS_SPHERE_CAMERA_DIRECTIONS[partName];
    if (!dir) {
        return null;
    }

    let rotation: ReadonlyQuat = dir.rotation;

    // Special rules for top/bottom view: preserve the camera's current heading
    // by snapping to the closest 90-degree Z-rotation.
    if (partName === "part_top" || partName === "part_bottom") {
        const z = partName === "part_top" ? 1 : -1;
        const currentDir = getCameraDir(currentCameraRotation as quat);
        if (vec3.dot(currentDir, vec3.fromValues(0, 0, z)) < -0.999) {
            // Already looking from above (one of NSWE top views) — orient north
            rotation = AXIS_SPHERE_CAMERA_DIRECTIONS[partName].rotation;
        } else {
            let minAngle = Infinity;
            let minAngleRotation: quat | null = null;
            for (const zAngle of [0, Math.PI / 2, Math.PI, (3 * Math.PI) / 2]) {
                const testRotation = makeRotation([0, 0, z], zAngle);
                const angle = quat.getAngle(currentCameraRotation, testRotation);
                if (angle < minAngle) {
                    minAngle = angle;
                    minAngleRotation = testRotation;
                }
            }
            rotation = minAngleRotation ?? rotation;
        }
    }

    if (quat.equals(currentCameraRotation, rotation)) {
        return null;
    }

    return rotation;
}

export function getPartName(currentCameraRotation: ReadonlyQuat, name: string): string {
    // NSWE geometry behaves differently when viewed from below
    if (name === "part_n" || name === "part_s" || name === "part_e" || name === "part_w") {
        const currentDir = getCameraDir(currentCameraRotation as quat);
        const up = vec3.fromValues(0, 0, 1);
        if (vec3.dot(currentDir, up) > 0) {
            return `${name}_bottom`;
        }
    }

    return name;
}

/**
 * Compute the new camera position after an axis sphere direction click.
 *
 * Position is computed differently depending on what context is available:
 * 1. If a bounding sphere is provided (selected objects), position the camera to frame it.
 * 2. If a view pick position is provided (center-screen pick), maintain the same distance.
 * 3. Otherwise, keep the current position unchanged.
 */
export function computeNewCameraPosition({
    directionRotation,
    currentPosition,
    boundingSphere,
    viewPickPosition,
    fov,
}: {
    directionRotation: ReadonlyQuat;
    currentPosition: ReadonlyVec3;
    boundingSphere?: { center: ReadonlyVec3; radius: number };
    viewPickPosition?: ReadonlyVec3;
    fov: number;
}): vec3 {
    const direction = getCameraDir(directionRotation as quat);

    if (boundingSphere) {
        // Position camera to frame the bounding sphere — formula matches novorender/api zoomTo
        const dist = Math.max(boundingSphere.radius / Math.tan(degToRad(fov) / 2), boundingSphere.radius);
        return vec3.scaleAndAdd(vec3.create(), boundingSphere.center, direction, -dist);
    }

    if (viewPickPosition) {
        // Maintain the same distance to the picked point, just change direction
        const distance = vec3.dist(currentPosition as vec3, viewPickPosition as vec3);
        return vec3.scaleAndAdd(vec3.create(), viewPickPosition as vec3, direction, -distance);
    }

    return vec3.clone(currentPosition as vec3);
}
